import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Loader2,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from "lucide-react";
import { DirectoryContextProvider, useDirectoryState } from "@/components/Directory/DirectoryContext";
import { DirectoryOrgPanel } from "@/components/Directory/DirectoryOrgPanel";
import { DirectoryUtilityBar } from "@/components/Directory/DirectoryUtilityBar";
import { signOut, useSession } from "@/auth/SessionProvider";
import { formatAtwReleaseLabel, getAtwDirectoryReleaseVersion } from "@/lib/appVersion";
import { authSignInUrl } from "@/lib/apiOrigin";
import {
  activeDirectoryApp,
  visibleDirectoryApps,
  visibleDirectoryModules,
  type DirectoryApp,
} from "@/lib/directoryAppRegistry";
import { cn } from "@/lib/utils";

const RAIL_EXPANDED = 256;
const RAIL_COLLAPSED = 72;
const COLLAPSE_KEY = "directory-sidebar-collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "true";
  } catch {
    return false;
  }
}

function moduleLinkClass(active: boolean, collapsed: boolean) {
  return cn(
    "relative z-[1] flex w-full cursor-pointer items-center gap-3.5 rounded-md border-l-[3px] py-2 text-left text-[13px] font-medium leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111829]",
    "[&_svg]:size-4 [&_svg]:shrink-0",
    collapsed ? "justify-center gap-0 border-l-0 px-2" : "pl-3 pr-2.5",
    active
      ? "border-sky-400 bg-slate-800 text-white shadow-sm [&_svg]:text-sky-200"
      : "border-transparent text-slate-300 hover:bg-slate-800/80 hover:text-white [&_svg]:text-slate-500",
  );
}

function userInitials(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

function SidebarRail({
  pathname,
  search,
  app,
  canManage,
  collapsed,
  showCollapseToggle,
  onToggleCollapsed,
  onNavigate,
}: {
  pathname: string;
  search: string;
  app: DirectoryApp;
  canManage: boolean;
  collapsed: boolean;
  showCollapseToggle?: boolean;
  onToggleCollapsed?: () => void;
  onNavigate?: () => void;
}) {
  const modules = visibleDirectoryModules(app, canManage);
  const versionLine = `ATW Directory · ${formatAtwReleaseLabel(getAtwDirectoryReleaseVersion())}`;

  return (
    <div
      data-directory-rail
      className={cn(
        "flex min-h-full min-w-0 grow flex-col gap-y-4 overflow-y-auto overflow-x-hidden border-r border-slate-800/90 py-5 shadow-md ring-1 ring-black/5",
        collapsed ? "px-2" : "px-3",
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center justify-center overflow-hidden",
          collapsed ? "h-14 px-0.5" : "h-20 px-1",
        )}
      >
        <Link
          to="/"
          onClick={onNavigate}
          aria-label="Directory home"
          className="flex h-full w-full items-center justify-center"
        >
          <img
            src={collapsed ? "/images/directory/network.png" : "/images/directory/wordmark.png"}
            alt="ATW Directory"
            className={cn("object-contain object-center", collapsed ? "h-10 w-10" : "h-full w-full")}
            loading="eager"
            decoding="async"
          />
        </Link>
      </div>
      <nav className="flex min-w-0 flex-1 flex-col" aria-label="Modules">
        <ul role="list" className="flex flex-1 flex-col gap-y-6">
          <li>
            {collapsed ? null : (
              <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                {app.label}
              </p>
            )}
            <ul className="space-y-px">
              {modules.map((item) => {
                const Icon = item.icon;
                const active = item.match(pathname, search);
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={moduleLinkClass(active, collapsed)}
                      title={collapsed ? item.label : undefined}
                      aria-current={active ? "page" : undefined}
                      onClick={onNavigate}
                    >
                      <Icon aria-hidden />
                      {collapsed ? (
                        <span className="sr-only">{item.label}</span>
                      ) : (
                        <span className="min-w-0 truncate">{item.label}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
          <li className="mt-auto border-t border-slate-700/80 pt-3">
            <div
              className={cn(
                "px-1 py-1",
                collapsed ? "flex flex-col items-center gap-1" : "flex items-center justify-end gap-1",
              )}
            >
              {showCollapseToggle && onToggleCollapsed ? (
                <button
                  type="button"
                  onClick={onToggleCollapsed}
                  className="inline-flex shrink-0 items-center justify-center rounded-md p-1.5 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80"
                  aria-label={collapsed ? "Expand sidebar" : "Retract sidebar"}
                >
                  {collapsed ? (
                    <PanelLeftOpen className="size-4 shrink-0" aria-hidden />
                  ) : (
                    <PanelLeftClose className="size-4 shrink-0" aria-hidden />
                  )}
                </button>
              ) : null}
            </div>
            {collapsed ? null : (
              <p
                className="mt-2 truncate px-1 text-center text-[10px] tracking-wide text-slate-400"
                title={versionLine}
              >
                {versionLine}
              </p>
            )}
          </li>
        </ul>
      </nav>
    </div>
  );
}

function ShellChrome() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const state = useDirectoryState();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [orgOpen, setOrgOpen] = useState(false);

  const canManage = state.status === "ready" && state.value.canManageUsers;
  const me = state.status === "ready" ? state.value.me : null;
  const userLabel =
    me?.name?.trim() ||
    me?.email ||
    session.data?.user?.name?.trim() ||
    session.data?.user?.email ||
    "Account";
  const userImageSrc = me?.profilePhoto || null;
  const sidebarWidth = collapsed ? RAIL_COLLAPSED : RAIL_EXPANDED;
  const apps = visibleDirectoryApps(canManage);
  const app = activeDirectoryApp(pathname);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname, search]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "true" : "false");
    } catch {
      /* ignore storage errors */
    }
  }, [collapsed]);

  if (session.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-zinc-950">
        <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
      </div>
    );
  }

  if (session.status === "unauthenticated") {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-slate-100 px-6 dark:bg-zinc-950">
        <div className="rounded-xl bg-[#111829] px-4 py-3">
          <img
            src="/images/directory/wordmark.png"
            alt="ATW Directory"
            className="h-14 w-auto max-w-[min(100%,360px)] object-contain"
          />
        </div>
        <p className="max-w-md text-center text-sm text-gray-600 dark:text-gray-400">
          Sign in with your ATW account to open Directory for your organization.
        </p>
        <a
          href={authSignInUrl(pathname)}
          className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900"
        >
          Sign in
        </a>
      </div>
    );
  }

  const rail = (collapsedRail: boolean, extras?: { showCollapseToggle?: boolean; onNavigate?: () => void }) => (
    <SidebarRail
      pathname={pathname}
      search={search}
      app={app}
      canManage={canManage}
      collapsed={collapsedRail}
      showCollapseToggle={extras?.showCollapseToggle}
      onToggleCollapsed={() => setCollapsed((value) => !value)}
      onNavigate={extras?.onNavigate}
    />
  );

  return (
    <div className="min-h-screen dark:bg-zinc-950" style={{ ["--directory-rail-w" as string]: `${sidebarWidth}px` }}>
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 xl:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-gray-900/80"
            aria-label="Close sidebar backdrop"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative flex h-full max-w-xs">
            <div className="w-full max-w-xs bg-[#111829]">{rail(false, { onNavigate: () => setMobileOpen(false) })}</div>
            <button
              type="button"
              className="absolute left-full top-4 ml-2 rounded-full bg-slate-800 p-2 text-white"
              onClick={() => setMobileOpen(false)}
              aria-label="Close sidebar"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>
      ) : null}

      <div className="relative hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:flex-col" style={{ width: sidebarWidth }}>
        {rail(collapsed, { showCollapseToggle: true })}
      </div>

      <div className="flex min-h-screen flex-col bg-white pb-10 dark:bg-zinc-950 xl:pl-[var(--directory-rail-w)]">
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs dark:border-white/5 dark:bg-gray-900 dark:shadow-none sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="-m-2.5 p-2.5 text-gray-900 dark:text-white xl:hidden"
            aria-label="Open sidebar"
          >
            <Menu className="size-5" />
          </button>

          <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto" aria-label="Apps">
            {apps.map((item) => {
              const active = item.id === app.id;
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold whitespace-nowrap",
                    active
                      ? "bg-gray-100 text-indigo-600 dark:bg-white/10 dark:text-white"
                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10",
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {canManage && app.id === "people" ? (
            <form
              className="hidden md:block"
              onSubmit={(event) => {
                event.preventDefault();
                const data = new FormData(event.currentTarget);
                const q = String(data.get("q") || "").trim();
                navigate(q ? `/people?q=${encodeURIComponent(q)}` : "/people");
              }}
            >
              <label className="relative block w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  type="search"
                  name="q"
                  placeholder="Search people"
                  aria-label="Search people"
                  className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none ring-indigo-500 placeholder:text-gray-400 focus:ring-2 dark:border-white/10 dark:bg-white/5 dark:text-white"
                />
              </label>
            </form>
          ) : null}

          <button
            type="button"
            onClick={() => setOrgOpen(true)}
            className="rounded-full p-0.5 text-gray-600 ring-2 ring-transparent hover:ring-indigo-200 dark:text-gray-300 dark:hover:ring-white/20"
            aria-label="Organization and account menu"
          >
            {userImageSrc ? (
              <img src={userImageSrc} alt="" className="size-9 rounded-full object-cover" />
            ) : (
              <span className="flex size-9 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
                {userInitials(userLabel) || "A"}
              </span>
            )}
          </button>
        </div>

        <main className="flex min-h-0 flex-1 flex-col bg-slate-100 p-3 sm:p-5 dark:bg-zinc-950">
          <div className="mx-auto flex min-h-full w-full max-w-[1920px] flex-1 flex-col overflow-x-auto rounded-xl border border-slate-200/90 bg-white shadow-sm dark:border-white/10 dark:bg-zinc-900">
            <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-6">
              {state.status === "loading" ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
                </div>
              ) : state.status === "error" ? (
                <div className="space-y-3">
                  <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-100">
                    {state.message}
                  </p>
                  <button
                    type="button"
                    onClick={() => signOut()}
                    className="text-sm font-medium text-gray-600 underline dark:text-gray-300"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </main>
      </div>

      <DirectoryUtilityBar />
      <DirectoryOrgPanel
        open={orgOpen}
        onOpenChange={setOrgOpen}
        userLabel={userLabel}
        userImageSrc={userImageSrc}
      />
    </div>
  );
}

export function DirectoryShell() {
  return (
    <DirectoryContextProvider>
      <ShellChrome />
    </DirectoryContextProvider>
  );
}
